import { LazyQueryExecFunction, OperationVariables } from '@parkchirp/api-client';
import { Dispatch, SetStateAction } from 'react';

import { SnackbarParams } from '../types';
import { FILE_UPLOAD_MAXIMUM } from './constants';
import { dayjs } from './dayjs';

export type FileUploadTableFile = {
	id: number;
	fileName: string;
	bucketPath: string;
	createdAt: string;
};

export async function addFileUploadTableFiles(
	newFiles: FileList,
	files: FileUploadTableFile[],
	bucket: string,
	bucketDir: string,
	includeTimestamp: boolean,
	getPutSignedUrl: LazyQueryExecFunction<any, OperationVariables>,
	setUploadingFiles: Dispatch<SetStateAction<string[]>>,
	setFiles: (files: SetStateAction<FileUploadTableFile[]>) => void | Promise<void>,
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackbarParams> | null>>,
) {
	const uploadTimestamp = dayjs().unix();

	const incomingFiles = [...newFiles];

	if (incomingFiles.length + files.length > FILE_UPLOAD_MAXIMUM) {
		setShowSnackbar({
			message: `Unable to add file(s): Max limit of 15 files has been reached.`,
			severity: 'warning',
		});
		return;
	}

	const newFileRows = incomingFiles.map((file, idx) => ({
		id: files.length + idx,
		fileName: includeTimestamp ? `${uploadTimestamp}-${file.name}` : file.name,
		bucketPath: '',
		createdAt: dayjs().format('YYYY-MM-DD'),
	}));
	const uploadingFileNames = newFileRows.map(({ fileName }) => fileName);
	setUploadingFiles((prevFiles) => [...prevFiles, ...uploadingFileNames]);

	try {
		const results = await Promise.allSettled(
			incomingFiles.map(async (file, i) => {
				const fileName = newFileRows[i].fileName;

				const putSignedUrlResponse = await getPutSignedUrl({
					variables: {
						bucket,
						s3Key: `${bucketDir}/${fileName}`,
					},
					context: {
						apiVersion: 'v2',
					},
					fetchPolicy: 'network-only',
				});

				const putSignedUrl = putSignedUrlResponse.data?.getPutSignedUrl?.url;
				if (!putSignedUrl) {
					throw new Error('Failed to get upload URL');
				}

				const response = await fetch(putSignedUrl, {
					method: 'PUT',
					headers: { 'Content-Type': file.type },
					body: file,
				});

				if (!response.ok) {
					throw new Error(`Upload failed with status ${response.status}`);
				}

				return newFileRows[i];
			}),
		);

		const successfulRows = results
			.filter((result): result is PromiseFulfilledResult<FileUploadTableFile> => result.status === 'fulfilled')
			.map((result) => result.value);
		const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');

		if (successfulRows.length) {
			await setFiles((currentFiles) => {
				const nextId = Math.max(-1, ...currentFiles.map(({ id }) => id)) + 1;
				return [...currentFiles, ...successfulRows.map((file, index) => ({ ...file, id: nextId + index }))];
			});
		}

		if (firstFailure) {
			const errorMessage =
				firstFailure.reason instanceof Error ? firstFailure.reason.message : String(firstFailure.reason);
			setShowSnackbar({
				message: `Unable to add file(s): ${errorMessage}`,
				severity: 'error',
			});
		}
	} catch (error) {
		setShowSnackbar({
			message: `Unable to add file(s): ${error.message}`,
			severity: 'error',
		});
	} finally {
		setUploadingFiles((prevFiles) =>
			prevFiles.filter((uploadedFile) => !uploadingFileNames.includes(uploadedFile)),
		);
	}
}

export const downloadCSVFile = (csvData, fileName) => {
	const CSVFile = new Blob([csvData], { type: 'text/csv' });
	const tempLink = document.createElement('a');
	tempLink.download = `${fileName}.csv`;
	const url = window.URL.createObjectURL(CSVFile);
	tempLink.href = url;
	tempLink.style.display = 'none';
	document.body.appendChild(tempLink);
	tempLink.click();
	document.body.removeChild(tempLink);
};

export const uploadDocToS3 = async (url: string, file: File): Promise<boolean> => {
	try {
		const objectURL = URL.createObjectURL(file);
		const documentBlob = await fetch(objectURL).then((r) => r.blob());

		const response = await fetch(url, {
			method: 'PUT',
			headers: { 'Content-Type': file.type },
			body: documentBlob,
		});

		if (response.ok) {
			return true;
		}

		return false;
	} catch {
		return false;
	}
};
